// src/context/NotificationContext.tsx
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Define the notification type
export type NotificationType = 'success' | 'error' | 'info' | 'warning';

// Define the notification interface
export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number; // in milliseconds
}

// Define the notification context interface
interface NotificationContextProps {
  notifications: Notification[];
  addNotification: (message: string, type: NotificationType, duration?: number) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

// Create the context
export const NotificationContext = createContext<NotificationContextProps>({} as NotificationContextProps);

// Custom hook to use the notification context
export const useNotification = () => useContext(NotificationContext);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Add a new notification
  const addNotification = useCallback((message: string, type: NotificationType = 'info', duration = 5000): string => {
    const id = Date.now().toString();
    
    const newNotification: Notification = {
      id,
      message,
      type,
      duration
    };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Automatically remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  }, []);

  // Remove a notification by id
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider 
      value={{ 
        notifications, 
        addNotification, 
        removeNotification, 
        clearAllNotifications 
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;